import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import ProfileCompletionModal from './ProfileCompletionModal';
import { getUserProfileService } from '../services';

const isProfileIncomplete = (profile) => {
    if (!profile) return true;
    const requiredFields = ['industry_group', 'region', 'primary_regulatory_profile', 'customer_size'];
    return requiredFields.some(field => !profile?.organization?.[field] || profile?.organization?.[field] === '');
};

const PrivateRoute = ({ children }) => {
    const isLoggedIn = !!sessionStorage.getItem("access_token");
    const navigate = useNavigate();
    const location = useLocation();

    const [showProfileModal, setShowProfileModal] = useState(false);
    const [profileData, setProfileData] = useState(null);
    const [profileChecked, setProfileChecked] = useState(false);

    useEffect(() => {
        if (!isLoggedIn) {
            setProfileChecked(true);
            return;
        }

        const checkProfile = async () => {
            try {
                // Try cached profile first
                const cached = localStorage.getItem('user_profile');
                if (cached) {
                    const parsed = JSON.parse(cached);
                    setProfileData(parsed);
                    if (isProfileIncomplete(parsed)) {
                        setShowProfileModal(true);
                    }
                    setProfileChecked(true);
                    return;
                }

                // No cache — fetch from API
                const profileResponse = await getUserProfileService();
                const fetchedProfileData = profileResponse?.statusCode?.response_data?.data;
                if (fetchedProfileData) {
                    localStorage.setItem('user_profile', JSON.stringify(fetchedProfileData));
                    setProfileData(fetchedProfileData);
                    if (isProfileIncomplete(fetchedProfileData)) {
                        setShowProfileModal(true);
                    }
                }
            } catch (err) {
                console.error('Error checking profile in PrivateRoute:', err);
            } finally {
                setProfileChecked(true);
            }
        };

        checkProfile();
    }, [isLoggedIn]);

    if (!isLoggedIn) {
        const intended = location.pathname + location.search;
        if (intended && intended !== '/') {
            sessionStorage.setItem('redirect_after_login', intended);
        }
        return <Navigate to="/" replace />;
    }

    if (!profileChecked) {
        // Avoid flash; wait until we know profile status
        return null;
    }

    return (
        <>
            {children}
            <ProfileCompletionModal
                show={showProfileModal}
                onHide={() => { }} // Cannot be closed manually
                onSuccess={() => {
                    setShowProfileModal(false);
                    // Refresh cached profile from localStorage (updated in modal on success)
                    const updated = localStorage.getItem('user_profile');
                    if (updated) setProfileData(JSON.parse(updated));
                }}
                existingProfile={profileData}
            />
        </>
    );
};

export default PrivateRoute;
